import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Trash, PaperPlaneTilt, Copy, CheckCircle, Clock } from "@phosphor-icons/react";

const ROLES = ["super_admin", "registrar", "approver", "printer_operator", "enforcement_officer", "auditor", "read_only"];

export default function DemoUsers() {
    const { t } = useI18n();
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ email: "", full_name: "", role: "read_only", ttl_days: 7 });
    const [lastLink, setLastLink] = useState(null);

    const load = () =>
        api.get("/admin/demo-users")
            .then((r) => setRows(r.data.users || []))
            .catch(() => toast.error("Failed to load demo users"));
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/admin/demo-users", form);
            const status = r.data.email_status || "sent";
            if (status === "sent") toast.success(`Invite emailed to ${form.email}`);
            else toast.warning(`User created but email failed: ${status}. Copy the link below to share manually.`);
            setLastLink({ email: form.email, url: r.data.invite_link, status });
            setForm({ email: "", full_name: "", role: "read_only", ttl_days: 7 });
            load();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Create failed");
        } finally { setBusy(false); }
    };

    const del = async (uid, email) => {
        if (!window.confirm(`Delete demo user ${email}?\n\nAll their sessions will be terminated immediately.`)) return;
        try {
            await api.delete(`/admin/demo-users/${uid}`);
            toast.success(`Deleted ${email}`);
            load();
        } catch { toast.error("Delete failed"); }
    };

    const resend = async (uid, email) => {
        setBusy(true);
        try {
            const r = await api.post(`/admin/demo-users/${uid}/resend`);
            const status = r.data.email_status || "sent";
            if (status === "sent") toast.success(`Invite resent to ${email}`);
            else toast.warning(`Email failed: ${status}. Copy the link to share manually.`);
            setLastLink({ email, url: r.data.invite_link, status });
            load();
        } catch { toast.error("Resend failed"); }
        finally { setBusy(false); }
    };

    const copyLink = (url) => {
        navigator.clipboard.writeText(url);
        toast.success("Invite link copied");
    };

    return (
        <div className="space-y-8" data-testid="demo-users-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· DEMO · EMAIL-INVITED USERS</div>
                <h1 className="text-4xl font-bold tracking-tight mt-2">{t("demoDemoUsers")}</h1>
                <p className="text-sm text-muted-foreground max-w-2xl mt-3">
                    Invite prospects by email. They receive a one-click magic link that signs them in without a password.
                    Delete an invite anytime — their sessions terminate immediately.
                </p>
            </div>

            <Card className="p-6">
                <form onSubmit={create} className="grid gap-4 md:grid-cols-[1fr_1fr_180px_120px_auto] md:items-end" data-testid="demo-user-form">
                    <div>
                        <Label htmlFor="di-email" className="text-[10px] tracking-widest uppercase font-bold">{t("email")}</Label>
                        <Input id="di-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="prospect@example.gov" data-testid="di-email" />
                    </div>
                    <div>
                        <Label htmlFor="di-name" className="text-[10px] tracking-widest uppercase font-bold">{t("fullName")}</Label>
                        <Input id="di-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Dr Aisha Karim" data-testid="di-name" />
                    </div>
                    <div>
                        <Label htmlFor="di-role" className="text-[10px] tracking-widest uppercase font-bold">{t("region")}</Label>
                        <select id="di-role" className="h-10 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="di-role">
                            {ROLES.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label htmlFor="di-ttl" className="text-[10px] tracking-widest uppercase font-bold">{t("demoDaysLabel")}</Label>
                        <Input id="di-ttl" type="number" min="1" max="30" value={form.ttl_days} onChange={(e) => setForm({ ...form, ttl_days: Number(e.target.value) })} data-testid="di-ttl" />
                    </div>
                    <Button type="submit" disabled={busy} data-testid="di-submit" className="gap-2 h-10">
                        <UserPlus size={16} weight="bold" />
                        {busy ? "Sending…" : "Send invite"}
                    </Button>
                </form>
            </Card>

            {lastLink && (
                <Card className="p-5 border-primary/40 bg-primary/5" data-testid="last-invite-link">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-primary font-bold mb-1">Latest invite link · {lastLink.email}</div>
                            <div className="mono text-xs break-all text-muted-foreground">{lastLink.url}</div>
                            <div className="text-[10px] uppercase tracking-widest mt-2">
                                Email status: <span className={lastLink.status === "sent" ? "text-emerald-500" : "text-primary"}>{lastLink.status}</span>
                            </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => copyLink(lastLink.url)} data-testid="copy-invite-link" className="gap-2 shrink-0">
                            <Copy size={14} weight="bold" /> Copy link
                        </Button>
                    </div>
                </Card>
            )}

            <Card className="p-0 overflow-hidden">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-muted-foreground">Invited users · {rows.length}</div>
                    <Button size="sm" variant="ghost" onClick={load} className="text-xs">{t("refresh")}</Button>
                </div>
                {rows.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground">{t("demoNoInvites")}</div>
                ) : (
                    <table className="w-full text-sm" data-testid="demo-users-table">
                        <thead>
                            <tr className="text-[10px] tracking-widest uppercase text-muted-foreground border-b border-border">
                                <th className="text-left px-5 py-3 font-bold">{t("email")}</th>
                                <th className="text-left px-5 py-3 font-bold">{t("adminName")}</th>
                                <th className="text-left px-5 py-3 font-bold">{t("region")}</th>
                                <th className="text-left px-5 py-3 font-bold">{t("status")}</th>
                                <th className="text-left px-5 py-3 font-bold">{t("createdAt")}</th>
                                <th className="text-right px-5 py-3 font-bold">{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={`row-${u.id}`}>
                                    <td className="px-5 py-3 mono text-xs">{u.email}</td>
                                    <td className="px-5 py-3">{u.full_name}</td>
                                    <td className="px-5 py-3"><span className="text-[10px] tracking-widest uppercase font-bold text-primary">{t(`role_${u.role}`)}</span></td>
                                    <td className="px-5 py-3">
                                        {u.invite_status === "used"
                                            ? <span className="inline-flex items-center gap-1.5 text-emerald-500 text-xs"><CheckCircle size={12} weight="fill" />Used</span>
                                            : <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs"><Clock size={12} weight="fill" />Pending</span>}
                                    </td>
                                    <td className="px-5 py-3 mono text-xs text-muted-foreground">{u.invite_expires_at?.slice(0, 10) || "—"}</td>
                                    <td className="px-5 py-3 text-right">
                                        <div className="inline-flex gap-1.5">
                                            <Button size="sm" variant="ghost" onClick={() => resend(u.id, u.email)} disabled={busy} data-testid={`resend-${u.id}`} className="gap-1.5 h-8 text-xs">
                                                <PaperPlaneTilt size={12} weight="bold" /> Resend
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => del(u.id, u.email)} data-testid={`delete-${u.id}`} className="gap-1.5 h-8 text-xs text-primary hover:text-primary">
                                                <Trash size={12} weight="bold" /> Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>
        </div>
    );
}
